"use client";

import { useState, useEffect } from "react";
import { getApiBaseUrl } from "@/lib/apiBase";

const base = () => getApiBaseUrl() || "";

/** Протоколгүй гадаад URL: host.tld/path эсвэл host.tld?query */
function looksLikeSchemelessUrl(s) {
  return (
    /^[a-z0-9][a-z0-9.-]*\.[a-z]{2,}\//i.test(s) ||
    /^[a-z0-9][a-z0-9.-]*\.[a-z]{2,}\?/i.test(s)
  );
}

/** uploads/foo.png гэх мэт — эхлээд / байхгүй ч зургийн өргөтгөлтэй */
function looksLikeRelativeImagePath(s) {
  if (!s || /^https?:\/\//i.test(s) || s.startsWith("//") || /^data:/i.test(s)) return false;
  if (s.startsWith("/")) return false;
  if (/[\s<>{}[\]`]/.test(s)) return false;
  return /\.(png|jpe?g|webp|gif|svg|ico|bmp)(\?[^]*)?$/i.test(s);
}

function joinBasePath(path) {
  const b = base().replace(/\/+$/, "");
  const p = path.startsWith("/") ? path : `/${path}`;
  return b ? `${b}${p}` : p;
}

function decodeHtmlEntities(s) {
  return s
    .replace(/&amp;/gi, "&")
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/g, "'")
    .replace(/&lt;/gi, "<")
    .replace(/&gt;/gi, ">");
}

function normalizeIconInput(icon) {
  let s = decodeHtmlEntities(String(icon ?? "").replace(/[\uFEFF\u200B-\u200D]/g, "").trim());
  if (!s) return "";
  if (s.startsWith("{") && s.includes('"')) {
    try {
      const o = JSON.parse(s);
      if (o && typeof o === "object") {
        s = String(o.url || o.src || o.icon || o.href || "").trim();
      }
    } catch {
      /* үл тоо */
    }
  }
  return s;
}

function resolveIconSrc(raw) {
  const s = raw;
  if (!s) return "";
  if (/^https?:\/\//i.test(s)) return s;
  if (s.startsWith("//")) return `https:${s}`;
  if (/^data:image\//i.test(s)) return s;
  if (s.startsWith("/")) {
    return joinBasePath(s);
  }
  if (looksLikeSchemelessUrl(s)) {
    return `https://${s}`;
  }
  if (looksLikeRelativeImagePath(s)) {
    return joinBasePath(s);
  }
  return "";
}

function isTextGlyph(raw) {
  const s = raw;
  if (!s) return false;
  if (/^https?:\/\//i.test(s) || s.startsWith("//")) return false;
  if (/^data:image\//i.test(s)) return false;
  if (s.startsWith("/")) return false;
  if (looksLikeSchemelessUrl(s)) return false;
  if (looksLikeRelativeImagePath(s)) return false;
  return true;
}

/**
 * DB-д icon нь зураг биш, Material Symbols Outlined-ийн нэр (restaurant, local_parking г.м)
 * partner-web BusinessesList-тэй ижил санаа — тухайн font шаардлагатай (layout-д ачаалсан).
 */
function materialSymbolGlyphName(raw) {
  const s = String(raw || "")
    .trim()
    .toLowerCase()
    .replace(/-/g, "_");
  if (s.length < 2 || s.length > 64) return "";
  if (!/^[a-z][a-z0-9_]*$/.test(s)) return "";
  return s;
}

function hasEmoji(s) {
  try {
    return /\p{Extended_Pictographic}/u.test(s);
  } catch {
    return /[\uD800-\uDBFF][\uDC00-\uDFFF]/.test(s);
  }
}

const boxCls = {
  sm: "h-4 w-4 min-w-[1rem] min-h-[1rem]",
  md: "h-5 w-5 min-w-[1.25rem] min-h-[1.25rem]",
  lg: "h-7 w-7 min-w-[1.75rem] min-h-[1.75rem]",
};

/**
 * Ангиллын icon: URL, харьцангуй зам, data URI, JSON {url}, эможи/текст.
 */
export function CategoryGlyph({ icon, size = "md", className = "" }) {
  const [imgFailed, setImgFailed] = useState(false);
  const raw = normalizeIconInput(icon);

  useEffect(() => {
    setImgFailed(false);
  }, [raw]);

  if (!raw) return null;

  const src = resolveIconSrc(raw);
  const textCls =
    size === "sm"
      ? "text-xs"
      : size === "lg"
        ? "text-xl"
        : "text-sm";
  const emojiCls = hasEmoji(raw) ? " text-[1.1em] leading-none tracking-normal" : "";

  if (src && !imgFailed) {
    return (
      <span
        className={`inline-flex shrink-0 items-center justify-center overflow-hidden rounded-sm ${boxCls[size] || boxCls.md} ${className}`}
        aria-hidden
      >
        <img
          src={src}
          alt=""
          className="max-h-full max-w-full h-full w-full object-contain"
          loading="lazy"
          decoding="async"
          referrerPolicy="no-referrer"
          onError={() => setImgFailed(true)}
        />
      </span>
    );
  }

  const materialName = !src && materialSymbolGlyphName(raw);
  if (materialName) {
    /* partner-web BusinessesList: shrink-0 + text-[20px] + өнгө */
    const symSize =
      size === "sm" ? "text-[16px]" : size === "lg" ? "text-[24px]" : "text-[20px]";
    return (
      <span
        className={`material-symbols-outlined inline-block shrink-0 text-[#577099] ${symSize} ${boxCls[size] || boxCls.md} ${className}`}
        aria-hidden
      >
        {materialName}
      </span>
    );
  }

  if (isTextGlyph(raw)) {
    return (
      <span
        className={`shrink-0 inline-flex items-center justify-center text-center leading-none opacity-90 ${boxCls[size] || boxCls.md} ${textCls}${emojiCls} ${
          hasEmoji(raw)
            ? "overflow-visible"
            : "overflow-hidden whitespace-nowrap text-ellipsis"
        } ${className}`}
        aria-hidden
        title={!hasEmoji(raw) && raw.length > 12 ? raw : undefined}
      >
        {raw}
      </span>
    );
  }

  if (src && imgFailed) {
    return (
      <span
        className={`inline-flex shrink-0 items-center justify-center rounded-sm bg-gray-200/90 text-gray-500 ${boxCls[size] || boxCls.md} ${textCls} ${className}`}
        aria-hidden
      >
        ·
      </span>
    );
  }

  return null;
}
